import { useEffect, useState } from "react";
import { getTeams, createTeam, updateTeam, deleteTeam } from "../api";

const EMPTY_FORM = { name: "", manager_name: "", manager_email: "", department: "", description: "" };

export default function Teams() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // team object being edited
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    getTeams().then(setTeams).catch((e) => setError(e.message)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setError(""); setShowForm(true); };
  const openEdit = (team) => {
    setEditing(team);
    setForm({ name: team.name, manager_name: team.manager_name, manager_email: team.manager_email, department: team.department || "", description: team.description || "" });
    setError("");
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editing) {
        await updateTeam(editing.id, form);
      } else {
        await createTeam(form);
      }
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete team "${name}"? This will unlink all associated requests.`)) return;
    try {
      await deleteTeam(id);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <>
      <div className="page-header">
        <h1>Teams</h1>
        <p>Manage organisational teams. Requests are linked to a team for cost and usage tracking.</p>
      </div>
      <div className="page-body">
        <div className="header-row">
          <div />
          <button className="btn btn-primary" onClick={openCreate}>+ Add Team</button>
        </div>

        {showForm && (
          <div className="card" style={{ marginBottom: 20, maxWidth: 620 }}>
            <div className="card-header">{editing ? `Edit — ${editing.name}` : "New Team"}</div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Team Name *</label>
                    <input value={form.name} onChange={(e) => set("name", e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label>Department</label>
                    <input value={form.department} onChange={(e) => set("department", e.target.value)} placeholder="e.g. Technology" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Manager Name *</label>
                    <input value={form.manager_name} onChange={(e) => set("manager_name", e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label>Manager Email *</label>
                    <input type="email" value={form.manager_email} onChange={(e) => set("manager_email", e.target.value)} required />
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea value={form.description} onChange={(e) => set("description", e.target.value)} style={{ minHeight: 60 }} />
                </div>
                {error && <div className="error-msg">{error}</div>}
                <div style={{ display: "flex", gap: 10 }}>
                  <button className="btn btn-primary" type="submit">{editing ? "Save Changes" : "Create Team"}</button>
                  <button className="btn btn-outline" type="button" onClick={() => setShowForm(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="card">
          <div className="table-wrap">
            {loading ? (
              <div className="empty">Loading…</div>
            ) : teams.length === 0 ? (
              <div className="empty">No teams yet. Add one to start tracking by team.</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Team</th>
                    <th>Department</th>
                    <th>Manager</th>
                    <th>Manager Email</th>
                    <th>Description</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {teams.map((t) => (
                    <tr key={t.id}>
                      <td><strong>{t.name}</strong></td>
                      <td className="text-muted">{t.department || "—"}</td>
                      <td>{t.manager_name}</td>
                      <td className="text-muted">
                        <a href={`mailto:${t.manager_email}`} style={{ color: "var(--primary)" }}>{t.manager_email}</a>
                      </td>
                      <td className="text-muted" style={{ fontSize: 13 }}>
                        {t.description ? t.description.slice(0, 60) + (t.description.length > 60 ? "…" : "") : "—"}
                      </td>
                      <td>
                        <div className="row-actions">
                          <button className="btn btn-outline btn-sm" onClick={() => openEdit(t)}>Edit</button>
                          <button className="btn btn-outline btn-sm" style={{ color: "var(--danger)" }} onClick={() => handleDelete(t.id, t.name)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
